import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/PageHeader";
import { fmtDate, money } from "@/lib/format";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

const statusClass = (status) =>
  status === "matched" ? "bg-success/10 text-success" :
  status === "mismatch" ? "bg-chart-4/10 text-chart-4" :
  status === "unreconciled" ? "bg-muted text-muted-foreground" : "bg-destructive/10 text-destructive";

export const Gstr2bTab = ({ rows2b, sum2b, busy2b, onPull, onUpload, onReconcile }) => {
  const fileRef = useRef(null);

  return (
    <div className="space-y-4" data-testid="gstr2b-view">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onPull} disabled={!!busy2b} data-testid="pull-2b-btn">
          {busy2b === "pull" ? "Pulling…" : "Pull from GSTN (simulated)"}
        </Button>
        <input ref={fileRef} type="file" accept=".json,.csv" className="hidden" data-testid="upload-2b-input"
          onChange={(e) => onUpload(e, fileRef)} />
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!busy2b} data-testid="upload-2b-btn">
          {busy2b === "upload" ? "Parsing…" : "Upload 2B JSON/CSV"}
        </Button>
        <Button size="sm" variant="outline" onClick={onReconcile} disabled={!!busy2b} data-testid="reconcile-2b-btn">
          {busy2b === "recon" ? "Matching…" : "Re-run reconciliation"}
        </Button>
      </div>

      {sum2b ? (
        <>
          <div className="grid gap-px border border-border bg-border sm:grid-cols-4" data-testid="2b-buckets">
            {Object.entries(sum2b.buckets).map(([k, b]) => (
              <div key={k} className="bg-background p-4" data-testid={`bucket-${k}`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.label}</p>
                <p className="num mt-1 text-lg font-semibold">{b.count}</p>
                <p className="num text-[11px] text-muted-foreground">{money(b.invoice_value)} · tax {money(b.tax)}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-px border border-border bg-border">
            {[["ITC eligible", sum2b.itc_eligible, "text-success"],
              ["ITC at risk (reversal)", sum2b.itc_at_risk, "text-destructive"],
              ["Rows compared", sum2b.total_rows, ""]].map(([k, v, cls]) => (
              <div key={k} className="flex-1 bg-background p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                <p className={`num mt-1 font-semibold ${cls}`}>
                  {typeof v === "number" && k !== "Rows compared" ? money(v) : v}
                </p>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {rows2b.length === 0 ? (
        <Empty label="No 2B data for this period" hint="Pull from GSTN or upload the portal JSON/CSV." />
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="dense-table w-full min-w-[900px]" data-testid="2b-rows-table">
            <thead>
              <tr><th>Supplier</th><th>GSTIN</th><th>Portal invoice</th><th>Date</th>
                <th className="text-right">Portal value</th><th className="text-right">Book value</th>
                <th className="text-right">Diff</th><th>Book voucher</th><th>Status</th><th>ITC</th></tr>
            </thead>
            <tbody>
              {rows2b.map((r) => (
                <tr key={r.id} className="row-in" data-testid={`2b-row-${r.id}`}>
                  <td className="max-w-[180px] truncate font-medium">{r.supplier_name || "—"}</td>
                  <td className="num">{r.supplier_gstin || "—"}</td>
                  <td className="num">{r.invoice_no}</td>
                  <td className="num whitespace-nowrap">{fmtDate(r.invoice_date)}</td>
                  <td className="num text-right">{money(r.invoice_value)}</td>
                  <td className="num text-right">{money(r.book_invoice_value || 0)}</td>
                  <td className={`num text-right ${Math.abs(r.difference || 0) > 0.5 ? "text-chart-4" : "text-muted-foreground"}`}>
                    {money(r.difference || 0)}
                  </td>
                  <td className="num text-muted-foreground">{r.matched_number || "—"}</td>
                  <td>
                    <span className={`whitespace-nowrap px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${statusClass(r.status)}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>{r.itc_available ? <CheckCircle size={14} className="text-success" /> : <XCircle size={14} className="text-destructive" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
